import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  useGridApiContext,
} from "@mui/x-data-grid";
import {
  FaCaretDown,
  FaFile,
  FaSearch,
  FaSearchMinus,
  FaTrash,
} from "react-icons/fa";
import {
  deleteHarmonization,
  editHarmonizationCompatibility,
  getHarmonizations,
  getNoCelexRelations,
  insertHarmonization,
  measureAddEuMeasure,
  measureListEuMeasures,
  measureRemoveEuMeasure,
} from "../../../../server/server";
import { notif } from "../../../utils/util";
import ChooseEuRegisterModal from "../../../modals/ChooseEuRegisterModal";
import ConfirmationModal from "../../../modals/ConfirmationModal";
import EditTUFileModal from "../../../modals/EditTUFileModal";
import { useUser } from "../../../../auth/UseUser";

const COMPACTIBILITY_MAP = {
  0: "није усклађен",
  1: "делимично усклађен",
  2: "потпуно усклађен",
};

const COMPACTIBILITY_MAP_REVERSE = {
  "није усклађен": "0",
  "делимично усклађен": "1",
  "потпуно усклађен": "2",
};

const EuRelationModal = ({
  open,
  register,
  onClose,
  isMeasure = false,
}: any) => {
  const { user } = useUser();
  const [value, setValue] = useState(0);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selRegister, setSelRegister] = useState<any>(null);
  const [noCelexRels, setNoCelexRels] = useState<any[]>([]);
  const [selNoCelexRel, setSelNoCelexRel] = useState<any>(null);
  const [items, setItems] = useState<any>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(6);
  const [rowCountState, setRowCountState] = useState(6);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<any>();
  const [compactibilities, setCompactibilities] = useState([
    "није усклађен",
    "делимично усклађен",
    "потпуно усклађен",
  ]);
  const [selTU, setSelTU] = useState<any>();
  const [tuModalIsOpen, setTuModalIsOpen] = useState(false);
  const [selComp, setSelComp] = useState("");

  const isAdmin = () => {
    return user?.roleView?.features?.includes("administration");
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const deleteRelation = async () => {
    if (isMeasure) {
      const resp = await measureRemoveEuMeasure(deleteId);
      if (resp.status === 200) {
        notif("Релација је успешно обрисана", "success");
        loadHarmonizations();
      } else {
        notif("Релација није обрисана", "error");
      }
    } else {
      const resp = await deleteHarmonization(deleteId);
      if (resp.status === 200) {
        notif("Релација је успешно обрисана", "success");
        loadHarmonizations(0);
      } else {
        notif("Релација није обрисана", "error");
      }
    }
  };

  const deleteRel = () => {
    deleteRelation();
    setDeleteModalIsOpen(false);
  };

  const closeDeleteModal = () => {
    setDeleteModalIsOpen(false);
  };

  const deleteItem = React.useCallback(
    (id: any) => () => {
      setTimeout(() => {
        setDeleteModalIsOpen(true);
        setDeleteId(id);
      });
    },
    [],
  );

  const createHarmonization = async () => {
    if (isMeasure) {
      if (selRegister?.celexNo) {
        setIsLoading(false);
        const resp = await measureAddEuMeasure(
          selRegister?.celexNo,
          parseInt(register.id),
        );
        if (resp?.status === 201) {
          setSelRegister(null);
          setSelComp("");
          setIsLoading(true);
          setRowCountState(6);
          setPage(0);
          loadHarmonizations();
          notif("Релација је успешно креирана", "success");
        } else {
          notif("Релација није креирана", "error");
        }
      } else {
        notif("Унос није успео: Обавезно је одабрати ЕУ пропис", "error");
      }
    } else {
      if ((selRegister?.celexNo && selComp) || (selNoCelexRel?.id && selComp)) {
        const resp = await insertHarmonization({
          celexNo: selRegister?.celexNo,
          nationalMeasure: parseInt(register.id),
          noCelexRelation: selNoCelexRel?.id,
          compatibility:
            COMPACTIBILITY_MAP_REVERSE[
              selComp as keyof typeof COMPACTIBILITY_MAP_REVERSE
            ],
        });
        if (resp?.status === 201) {
          setSelRegister(null);
          setSelNoCelexRel(null);
          setSelComp("");
          setIsLoading(true);
          setRowCountState(6);
          setPage(0);
          loadHarmonizations(0);
          notif("Релација је успешно креирана", "success");
        } else {
          notif("Релација није креирана", "error");
        }
      } else {
        notif(
          `Унос није успео: Обавезно је одабрати ${
            selComp ? "ЕУ пропис" : "степен усклађености"
          }`,
          "error",
        );
      }
    }
  };

  const loadNoCelexRels = async () => {
    const resp = await getNoCelexRelations();

    if (resp?.status === 200) {
      setNoCelexRels(resp.data);
    }
  };

  const loadHarmonizations = async (offset: any = 0) => {
    if (isMeasure) {
      const resp = await measureListEuMeasures(register.measureId);
      if (resp?.status === 200) {
        setItems(resp.data?.harmonizations || []);
        setRowCountState(resp?.data?.total || 0);
        setIsLoading(false);
      }
    } else {
      const resp = await getHarmonizations(
        offset,
        pageSize,
        register.natMeasureId,
      );
      if (resp?.status === 200) {
        setItems(resp.data?.harmonizations);
        setRowCountState(resp?.data?.total);
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (open && register?.measureId) {
      if (!isMeasure) {
        loadNoCelexRels();
      }
    }
    loadHarmonizations(0);
  }, [open, register]);

  const closeTUModal = () => {
    setTuModalIsOpen(false);
  };

  const changeComp = async (newComp: any, id: any) => {
    const resp = await editHarmonizationCompatibility({
      id,
      compatibility:
        COMPACTIBILITY_MAP_REVERSE[
          newComp as keyof typeof COMPACTIBILITY_MAP_REVERSE
        ],
    });
    if (resp?.status === 201) {
      setIsLoading(true);
      setItems([]);
      loadHarmonizations(0);
      notif("Усклађеност је успешно ажурирана", "success");
    }
  };

  const CustomComponent = (params: any) => {
    const ref = useGridApiContext();
    return (
      <div
        className="flex items-center justify-between w-full"
        onClick={(event) => {
          event.stopPropagation();
          ref.current.startCellEditMode({
            id: params.id,
            field: params.field,
          });
        }}
      >
        <div>
          {COMPACTIBILITY_MAP[params?.value as keyof typeof COMPACTIBILITY_MAP]}
        </div>
        <FaCaretDown className="fill-[#707070]" />
      </div>
    );
  };

  const setPageAndCall = (newPage: any) => {
    setPage(newPage);
    loadHarmonizations(newPage * pageSize);
  };

  const editUser = React.useCallback(
    (tu: any) => () => {
      setSelTU(tu);
      setTuModalIsOpen(true);
    },
    [],
  );

  const measureColumns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "celexNo",
        headerName: "Celex ознака",
        flex: 1,
        maxWidth: 140,
        minWidth: 140,
        renderCell: (params) => params?.value ?? "-",
      },
      {
        field: "name",
        headerName: "Назив прописа ЕУ (на енглеском језику)",
        flex: 1,
      },
      {
        field: "username",
        headerName: "Корисник",
        maxWidth: 150,
        minWidth: 150,
        flex: 1,
      },
    ],
    [deleteItem],
  );

  const nationalColumns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "celexNo",
        headerName: "Celex ознака",
        flex: 1,
        maxWidth: 140,
        minWidth: 140,
        renderCell: (params) => params?.value ?? "-",
      },
      {
        field: "name",
        headerName: "Назив прописа ЕУ (на енглеском језику)",
        flex: 1,
      },
      {
        field: "compatibility",
        headerName: "Усклађеност",
        maxWidth: 200,
        minWidth: 200,
        renderCell: (params) =>
          COMPACTIBILITY_MAP[
            params.row.compatibility as keyof typeof COMPACTIBILITY_MAP
          ] ?? "-",
      },
      {
        field: "username",
        headerName: "Корисник",
        maxWidth: 150,
        minWidth: 150,
        flex: 1,
      },
    ],
    [deleteItem, isAdmin],
  );

  const columns = isMeasure ? measureColumns : nationalColumns;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        sx={{ zIndex: 1 }}
      >
        <DialogTitle>
          <span className="text-[#04438b] text-2xl font-bold">
            Веза са европским прописом (CELEX)
          </span>
          <div className="text-[#04438b] text-2xl font-bold mt-2">
            {register?.title || register?.name}
          </div>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: "100%", typography: "body1", mt: 2 }}>

            {false && (isMeasure || value === 0) && (
              <Box sx={{ p: 3 }}>
                <div className="flex items-start gap-6 flex-col">
                  <button
                    className="h-[40px] w-[350px] border-[#81848f3d] border bg-white hover:bg-[#d9d9d9] rounded-md cursor-pointer"
                    onClick={() => setModalIsOpen(true)}
                  >
                    {selRegister ? (
                      <div className="flex px-4 justify-start text-left items-center">
                        <FaSearchMinus className="fill-[#404040]" />
                        <p className="text-darkBlue w-full pl-3">
                          {selRegister?.celexNo}
                        </p>
                      </div>
                    ) : (
                      <div className="flex px-4 justify-between items-center">
                        <FaSearch className="fill-[#404040]" />
                        <p className="text-[#404040] w-full pl-3">
                          Одабир ЕУ прописа
                        </p>
                      </div>
                    )}
                  </button>
                  {!isMeasure && (
                    <div className="flex items-center gap-6">
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="demo-select-small">
                          Усклађеност
                        </InputLabel>
                        <Select
                          labelId="demo-select-small"
                          id="demo-select-small"
                          value={selComp || ""}
                          onChange={(e) => setSelComp(e.target.value)}
                          label="Усклађеност"
                          fullWidth
                          className="capitalize"
                          disabled
                        >
                          {compactibilities?.map((c) => {
                            return (
                              <MenuItem
                                value={c}
                                className="capitalize"
                                key={c}
                              >
                                {c}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </div>
                  )}
                </div>
              </Box>
            )}

            {false && !isMeasure && value === 1 && (
              <Box sx={{ p: 3 }}>
                <div className="flex items-start gap-6 flex-col">
                  <Autocomplete
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option.id}>
                          {option.name}
                        </li>
                      );
                    }}
                    value={selNoCelexRel}
                    size="small"
                    onChange={(e: any, newValue: any) => {
                      setSelNoCelexRel(newValue);
                    }}
                    id="controllable-states-demo"
                    options={noCelexRels}
                    getOptionLabel={(option) => option?.name}
                    sx={{ width: 500 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Назив међ. кон. која нема број, али је релевантна за ЕУ"
                      />
                    )}
                  />
                  <div className="flex items-center gap-6">
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel id="demo-select-small">
                        Усклађеност
                      </InputLabel>
                      <Select
                        labelId="demo-select-small"
                        id="demo-select-small"
                        value={selComp}
                        onChange={(e) => {
                          setSelComp(e.target.value);
                        }}
                        label="Усклађеност"
                        fullWidth
                        className="capitalize"
                        disabled
                      >
                        {compactibilities?.map((c) => {
                          return (
                            <MenuItem value={c} className="capitalize" key={c}>
                              {c}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </div>
                </div>
              </Box>
            )}
          </Box>

          {/* <div className='w-full flex justify-end items-center mb-4'>
						<Button variant='contained' sx={{ backgroundColor: '#0F2765' }} onClick={createHarmonization}>
							{isMeasure ? 'Додај' : 'Потврди'}
						</Button>
					</div> */}

          {items?.length > 0 && (
            <div>
              <div className="text-[#04438b] mb-2">
                {isMeasure
                  ? "Повезаност са ЕУ прописом"
                  : "Повезаност са ЕУ прописом"}
              </div>
              <div className="h-[430px] w-full">
                <DataGrid
                  rowCount={rowCountState}
                  loading={isLoading}
                  pagination
                  disableColumnMenu
                  paginationMode="server"
                  onPageChange={(newPage) => setPageAndCall(newPage)}
                  onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                  page={page}
                  pageSize={pageSize}
                  rows={items}
                  columns={columns}
                  style={{ zIndex: 0 }}
                  rowsPerPageOptions={[6]}
                  disableSelectionOnClick
                  components={{
                    NoRowsOverlay: () => (
                      <Stack
                        height="100%"
                        alignItems="center"
                        justifyContent="center"
                      >
                        Не постоје везе са ЕУ прописима
                      </Stack>
                    ),
                    NoResultsOverlay: () => (
                      <Stack
                        height="100%"
                        alignItems="center"
                        justifyContent="center"
                      >
                        Не постоје везе са ЕУ прописима
                      </Stack>
                    ),
                  }}
                />
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <div
            className="border border-[#81848f] hover:bg-[#81848f] hover:text-white cursor-pointer flex items-center justify-center border-redError text-redError text-base font-semibold rounded-lg w-[250px] h-[55px]"
            onClick={onClose}
          >
            Затвори
          </div>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        modalIsOpen={deleteModalIsOpen}
        closeModal={closeDeleteModal}
        confirmation={deleteRel}
        message={`Обриши релацију са прописом?`}
      />

      <ChooseEuRegisterModal
        open={modalIsOpen}
        modalIsOpen={modalIsOpen}
        closeModal={closeModal}
        setSelRegister={setSelRegister}
        selRegister={selRegister}
      />

      {!isMeasure && (
        <EditTUFileModal
          modalIsOpen={tuModalIsOpen}
          closeModal={closeTUModal}
          confirmation={() => loadHarmonizations(0)}
          fileId={selTU?.fileId}
          id={selTU?.tuid}
        />
      )}
    </>
  );
};

export default EuRelationModal;
